import React, { memo, useRef, useLayoutEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

function ScaledIframe({ url, scaledWidth = null, displayWidth = null, aspectRatio = 1 }) {
  const wrapperRef = useRef(null);
  const [maxContainerHeight, setMaxContainerHeight] = useState(null);

  const shouldScale = scaledWidth && displayWidth;

  useLayoutEffect(() => {
    if (wrapperRef.current && shouldScale) {
      const parent = wrapperRef.current.parentElement;

      if (parent) {
        const { height } = parent.getBoundingClientRect();
        setMaxContainerHeight(height);
      }
    }
  }, [displayWidth, scaledWidth, shouldScale]);

  const { finalDisplayWidth, finalDisplayHeight, scale, scaledHeight } = useMemo(() => {
    if (!shouldScale || !aspectRatio) {
      return {
        finalDisplayWidth: displayWidth,
        finalDisplayHeight: null,
        scale: 1,
        scaledHeight: null,
      };
    }

    const mediaHeight = displayWidth / aspectRatio;
    const isHeightLimited = maxContainerHeight && mediaHeight >= maxContainerHeight;

    const finalWidth = isHeightLimited ? maxContainerHeight * aspectRatio : displayWidth;
    const finalHeight = isHeightLimited ? maxContainerHeight : mediaHeight;
    const calculatedScale = finalWidth / scaledWidth;
    const calculatedScaledHeight = isHeightLimited
      ? finalHeight / calculatedScale
      : scaledWidth / aspectRatio;

    return {
      finalDisplayWidth: finalWidth,
      finalDisplayHeight: finalHeight,
      scale: calculatedScale,
      scaledHeight: calculatedScaledHeight,
    };
  }, [shouldScale, aspectRatio, displayWidth, maxContainerHeight, scaledWidth]);

  const wrapperStyle = shouldScale
    ? {
        width: `${finalDisplayWidth}px`,
        height: finalDisplayHeight ? `${finalDisplayHeight}px` : 'auto',
        overflow: 'hidden',
      }
    : {
        width: '100%',
        height: '100%',
      };

  const iframeStyle = {
    aspectRatio,
    border: 'none',
    ...(shouldScale && {
      width: `${scaledWidth}px`,
      height: scaledHeight ? `${scaledHeight}px` : 'auto',
      transform: `scale(${scale})`,
      transformOrigin: '0 0',
    }),
  };

  return (
    <div ref={wrapperRef} {...(wrapperStyle && { style: wrapperStyle })}>
      <iframe src={url} style={iframeStyle} title="Preview" loading="lazy" tabIndex={-1} />
    </div>
  );
}

ScaledIframe.propTypes = {
  url: PropTypes.string.isRequired,
  scaledWidth: PropTypes.number,
  displayWidth: PropTypes.number,
  aspectRatio: PropTypes.number,
};

export default memo(ScaledIframe);
